import React, { useState, useEffect, useCallback } from 'react';
import type { GlobalDocument } from '../../types';
import { adminService } from '../../services/adminService';
import apiClient from '../../services/api';

interface IRCSection {
  ruleNumber: string;
  title: string;
  pageNumber: number;
}

const IRCRulesManager: React.FC = () => {
  const [currentDocument, setCurrentDocument] = useState<GlobalDocument | null>(null);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [sections, setSections] = useState<IRCSection[]>([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  const fetchDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    setDocumentsError(null);
    try {
      const docs = await adminService.getGlobalDocuments();
      const ircDoc = docs.find(d => d.type === 'irc_rules');
      setCurrentDocument(ircDoc || null);
      if (ircDoc && ircDoc.extractionStatus === 'processing') {
        setPolling(true);
      }
    } catch {
      setDocumentsError(null);
    } finally {
      setDocumentsLoading(false);
    }
  }, []);

  const fetchSections = useCallback(async () => {
    setSectionsLoading(true);
    try {
      const resp = await apiClient.get<{ sections: IRCSection[]; count: number }>('/irc-rules/sections');
      setSections(resp.data.sections || []);
    } catch {
      setSections([]);
    } finally {
      setSectionsLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  useEffect(() => {
    if (currentDocument && currentDocument.extractionStatus === 'complete') {
      fetchSections();
    }
  }, [currentDocument, fetchSections]);

  useEffect(() => {
    if (!polling) return;
    const id = setInterval(async () => {
      try {
        const docs = await adminService.getGlobalDocuments();
        const ircDoc = docs.find(d => d.type === 'irc_rules');
        setCurrentDocument(ircDoc || null);
        if (ircDoc && ircDoc.extractionStatus !== 'processing') {
          setPolling(false);
          if (ircDoc.extractionStatus === 'complete') fetchSections();
        }
      } catch { /* continue */ }
    }, 5000);
    return () => clearInterval(id);
  }, [polling, fetchSections]);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    try {
      const result = await adminService.uploadGlobalDocument(uploadFile, 'irc_rules');
      setUploadSuccess(`Upload started. Document ID: ${result.documentId}`);
      setCurrentDocument(result);
      setPolling(true);
      setUploadFile(null);
      const fi = document.getElementById('irc-rules-file') as HTMLInputElement;
      if (fi) fi.value = '';
    } catch {
      setUploadError('Failed to upload IRC Rules PDF.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentDocument) return;
    if (!window.confirm('Delete IRC Rules and all extracted sections? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/admin/documents/global/${currentDocument.documentId}`);
      setCurrentDocument(null);
      setSections([]);
    } catch {
      alert('Failed to delete IRC Rules.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="irc-rules-manager" data-testid="irc-rules-manager">
      <h2>IRC Rules</h2>

      <div data-testid="document-section">
        <h3>Current Document</h3>
        {documentsLoading && <p>Loading document information…</p>}
        {documentsError && <div className="error-message">{documentsError}</div>}
        {!documentsLoading && !documentsError && !currentDocument && (
          <div className="info-message">No IRC Rules have been uploaded yet.</div>
        )}
        {!documentsLoading && !documentsError && currentDocument && (
          <table className="global-documents-table">
            <thead><tr><th>Filename</th><th>Status</th><th>Sections</th><th>Uploaded</th><th></th></tr></thead>
            <tbody><tr>
              <td>{currentDocument.filename}</td>
              <td>{currentDocument.extractionStatus || 'pending'}</td>
              <td>{currentDocument.sectionCount || 0}</td>
              <td>{new Date(currentDocument.uploadedAt).toLocaleString()}</td>
              <td>
                <button onClick={handleDelete} disabled={deleting} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                  🗑️
                </button>
              </td>
            </tr></tbody>
          </table>
        )}
      </div>

      {currentDocument && currentDocument.extractionStatus === 'processing' && (
        <div className="info-message">
          <strong>Extraction in progress…</strong>
          {currentDocument.extractionProgress && <div style={{ marginTop: '8px' }}>{currentDocument.extractionProgress}</div>}
        </div>
      )}
      {currentDocument && currentDocument.extractionStatus === 'complete' && (
        <div className="success-message">Extraction complete. {currentDocument.sectionCount} sections extracted.</div>
      )}
      {currentDocument && currentDocument.extractionStatus === 'failed' && (
        <div className="error-message">Extraction failed.{currentDocument.extractionError && ` Error: ${currentDocument.extractionError}`}</div>
      )}

      <form onSubmit={handleUpload}>
        <h3>Upload IRC Rules PDF</h3>
        {uploadSuccess && <div className="success-message">{uploadSuccess}</div>}
        {uploadError && <div className="error-message">{uploadError}</div>}
        <div className="form-field">
          <label htmlFor="irc-rules-file">PDF File:</label>
          <input id="irc-rules-file" type="file" accept=".pdf,application/pdf" onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)} />
        </div>
        <div className="form-actions">
          <button type="submit" disabled={uploading || !uploadFile}>{uploading ? 'Uploading…' : 'Upload & Extract'}</button>
        </div>
      </form>

      {currentDocument && currentDocument.extractionStatus === 'complete' && (
        <div>
          <h3>Extraction Results</h3>
          {sectionsLoading && <p>Loading sections…</p>}
          {!sectionsLoading && sections.length > 0 && (() => {
            const sorted = [...sections].sort((a, b) => {
              const na = parseFloat(a.ruleNumber) || 999;
              const nb = parseFloat(b.ruleNumber) || 999;
              if (na !== nb) return na - nb;
              return a.ruleNumber.localeCompare(b.ruleNumber);
            });
            // Group: top-level = no dot or non-numeric, sub = has dot
            const groups: { parent: IRCSection; children: IRCSection[] }[] = [];
            let current: { parent: IRCSection; children: IRCSection[] } | null = null;
            for (const s of sorted) {
              const isTopLevel = !s.ruleNumber.includes('.') || isNaN(Number(s.ruleNumber.split('.')[0]));
              if (isTopLevel) {
                current = { parent: s, children: [] };
                groups.push(current);
              } else if (current) {
                current.children.push(s);
              } else {
                current = { parent: s, children: [] };
                groups.push(current);
              }
            }
            const toggleGroup = (rn: string) => {
              setExpandedGroups(prev => {
                const next = new Set(prev);
                if (next.has(rn)) next.delete(rn); else next.add(rn);
                return next;
              });
            };
            return (
              <div>
                <p>Total: {sections.length} sections across {groups.length} groups</p>
                {groups.map(g => {
                  const expanded = expandedGroups.has(g.parent.ruleNumber);
                  return (
                    <div key={g.parent.ruleNumber} className="category-section">
                      <div className="category-header" onClick={() => toggleGroup(g.parent.ruleNumber)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        <span style={{ marginRight: '8px' }}>{g.children.length > 0 ? (expanded ? '▼' : '▶') : '•'}</span>
                        <h4 style={{ display: 'inline' }}>{g.parent.ruleNumber} {g.parent.title}</h4>
                        {g.children.length > 0 && (
                          <span className="requirement-count"> ({g.children.length} sub-sections)</span>
                        )}
                      </div>
                      {expanded && g.children.length > 0 && (
                        <div className="category-details">
                          <table className="requirements-table">
                            <thead><tr><th>Rule</th><th>Title</th></tr></thead>
                            <tbody>
                              {g.children.map(c => (
                                <tr key={c.ruleNumber}><td>{c.ruleNumber}</td><td>{c.title}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
          {!sectionsLoading && sections.length === 0 && <p>No sections found.</p>}
        </div>
      )}
    </div>
  );
};

export default IRCRulesManager;
